/**
 * API HTTP client with in-memory access token, single-flight refresh,
 * correlation ID, idempotency key, session-expired events, locale, CSRF,
 * timeout, typed errors, and abort support.
 *
 * P3-004: Hardened HTTP replay.
 *
 * Access token is stored in memory only — never in localStorage.
 * Refresh uses HttpOnly cookie — never passes token in body.
 */
import type { ApiErrorDetails, ApiErrorEnvelope, ErrorCode } from '@finance/contracts';
import { ERROR_CODE_KEYS, ERROR_CODES } from '@finance/contracts';
import {
  AUTHORIZATION,
  ACCEPT_LANGUAGE,
  X_REQUEST_ID,
  X_CORRELATION_ID,
  X_CSRF_TOKEN,
  IDEMPOTENCY_KEY,
  NON_IDEMPOTENT_METHODS,
} from '@finance/contracts/http/request-headers';

// In-memory access token — never persisted
let _accessToken: string | null = null;
let _refreshInFlight: Promise<string> | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

/** Client-only failure codes that are never returned by the API. */
export type ClientErrorCode = 'NETWORK_ERROR' | 'TIMEOUT' | 'ABORTED';

export type ApiErrorCode = ErrorCode | ClientErrorCode;

export class ApiError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    public readonly status: number,
    public readonly key: string,
    message: string,
    public readonly details?: ApiErrorDetails,
    public readonly correlationId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Session-expired event dispatched on auth expiration */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

function emitSessionExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

function isApiErrorEnvelope(body: unknown): body is ApiErrorEnvelope {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as Record<string, unknown>).error === 'object' &&
    (body as Record<string, unknown>).error !== null &&
    'code' in ((body as Record<string, unknown>).error as Record<string, unknown>) &&
    typeof ((body as Record<string, unknown>).error as Record<string, unknown>).code === 'string'
  );
}

const CANONICAL_ERROR_CODES: Set<ApiErrorCode> = new Set([
  ...ERROR_CODES,
  'NETWORK_ERROR',
  'TIMEOUT',
  'ABORTED',
]);

function normalizeApiErrorCode(raw: string): ApiErrorCode {
  const code = raw.trim().toUpperCase();
  // Map legacy/alternate API codes to the canonical token reuse code.
  const mappedCode = code === 'AUTH_REFRESH_REUSED' ? 'AUTH_TOKEN_REUSE' : code;
  return CANONICAL_ERROR_CODES.has(mappedCode as ApiErrorCode)
    ? (mappedCode as ApiErrorCode)
    : 'INTERNAL_ERROR';
}

async function attemptRefresh(): Promise<string> {
  if (_refreshInFlight) {
    return _refreshInFlight;
  }

  _refreshInFlight = (async () => {
    try {
      const res = await httpClient('/api/auth/refresh', {
        method: 'POST',
        skipAuth: true,
        skipRefresh: true,
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        const err = await parseApiError(res.clone());
        if (err.code === 'AUTH_TOKEN_REUSE') {
          throw new ApiError('AUTH_TOKEN_REUSE', err.status, err.key, err.message, err.details);
        }
        throw new ApiError('AUTH_SESSION_EXPIRED', err.status, err.key, err.message, err.details);
      }

      const body = await res.json();
      const token =
        isApiErrorEnvelope(body) && body.error
          ? undefined
          : (body as { data?: { accessToken?: string } }).data?.accessToken;

      if (!token) {
        throw new ApiError(
          'AUTH_SESSION_EXPIRED',
          res.status,
          'error.auth.tokenExpired',
          'error.auth.tokenExpired'
        );
      }

      _accessToken = token;
      return token;
    } catch (err) {
      if (err instanceof ApiError) throw err;
      throw new ApiError(
        'NETWORK_ERROR',
        0,
        'common.errorState.networkError',
        err instanceof Error ? err.message : 'common.errorState.networkError'
      );
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
}

/**
 * Restore an access token through the same process-wide single-flight used by
 * 401 replay. React remounts and simultaneous API failures must never rotate
 * the same HttpOnly refresh token twice.
 */
export function refreshAccessToken(): Promise<string> {
  return attemptRefresh();
}

/** Auth endpoints that must not trigger refresh-on-401 */
const AUTH_ENDPOINTS = new Set([
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  // Credential verification failures are an expected result of an authenticated
  // password-change attempt; they must not rotate the caller's session or replay
  // a non-idempotent security operation.
  '/api/auth/me/password',
  '/api/auth/mfa/enroll',
  '/api/auth/mfa/verify',
  '/api/auth/mfa/disable',
  '/api/auth/mfa/backup-codes',
]);

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.has(url.split('?')[0]);
}

function resolveApiUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL as string | undefined;
  if (!apiBaseUrl) return url;
  const base = String(apiBaseUrl);
  if (/^https?:\/\//i.test(base)) {
    return new URL(url, base).toString();
  }
  // base is a relative path (e.g. /api), url is either /api/auth/login or auth/login
  if (url === base || url.startsWith(`${base}/`)) return url;
  return `${base.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

export interface HttpClientOptions extends RequestInit {
  timeout?: number;
  skipAuth?: boolean;
  /** Skip the internal refresh-on-401 logic (caller handles refresh itself) */
  skipRefresh?: boolean;
  /** Explicit idempotency key — if not provided, one is generated for non-idempotent requests */
  idempotencyKey?: string;
}

function readDocumentLocale(): string {
  if (typeof document !== 'undefined') {
    return document.documentElement.lang || 'en';
  }
  return 'en';
}

function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.querySelector<HTMLMetaElement>('meta[name="x-csrf-token"]')?.content ?? undefined;
}

function mergeAbortSignals(
  timeoutMs: number,
  external?: AbortSignal | null
): { signal: AbortSignal; cleanup: () => void; wasTimedOut: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const merged = external ? AbortSignal.any([external, controller.signal]) : controller.signal;

  const cleanup = () => {
    clearTimeout(timeoutId);
  };

  return { signal: merged, cleanup, wasTimedOut: () => timedOut };
}

/**
 * httpClient — makes authenticated API requests.
 *
 * - Injects in-memory access token in Authorization header
 * - On 401, attempts one refresh via HttpOnly cookie and replays the request
 * - Generates idempotency keys for non-idempotent commands (initial and replay)
 * - Emits session-expired event on terminal auth failures
 * - Propagates correlation ID (X-Request-ID) and locale
 * - Supports abort signals and timeouts
 */
export async function httpClient(url: string, options: HttpClientOptions = {}): Promise<Response> {
  const {
    timeout = 30_000,
    skipAuth = false,
    skipRefresh = false,
    signal,
    idempotencyKey,
    ...init
  } = options;

  const { signal: mergedSignal, cleanup, wasTimedOut } = mergeAbortSignals(timeout, signal);
  const headers = new Headers(init.headers);
  const method = (init.method ?? 'GET').toUpperCase();
  const isNonIdempotent =
    (NON_IDEMPOTENT_METHODS as readonly string[]).includes(method) && !isAuthEndpoint(url);

  // Generate an idempotency key once so the same key is used for any replay.
  const requestIdempotencyKey =
    idempotencyKey ?? (isNonIdempotent ? crypto.randomUUID() : undefined);

  // Generate correlation ID once and reuse for refresh replay
  const requestId = crypto.randomUUID();

  if (!skipAuth && _accessToken) {
    headers.set(AUTHORIZATION, `Bearer ${_accessToken}`);
  }

  // Correlation ID for distributed tracing (reused on replay)
  headers.set(X_REQUEST_ID, requestId);

  // Locale header
  headers.set(ACCEPT_LANGUAGE, readDocumentLocale());

  // CSRF token from meta tag (if present)
  const csrfToken = readCsrfToken();
  if (csrfToken) {
    headers.set(X_CSRF_TOKEN, csrfToken);
  }

  // Idempotency key for non-idempotent requests
  if (requestIdempotencyKey) {
    headers.set(IDEMPOTENCY_KEY, requestIdempotencyKey);
  }

  try {
    const requestUrl = resolveApiUrl(url);
    let res = await fetch(requestUrl, {
      ...init,
      headers,
      credentials: 'include',
      signal: mergedSignal,
    });

    // One refresh attempt on 401 — but never for auth endpoints
    if (res.status === 401 && !skipAuth && !skipRefresh && !isAuthEndpoint(url)) {
      try {
        const newToken = await attemptRefresh();
        headers.set(AUTHORIZATION, `Bearer ${newToken}`);
        // Reuse the same correlation ID and idempotency key for replay
        headers.set(X_REQUEST_ID, requestId);
        res = await fetch(requestUrl, {
          ...init,
          headers,
          credentials: 'include',
          signal: mergedSignal,
        });
      } catch (err) {
        _accessToken = null;
        if (
          err instanceof ApiError &&
          (err.code === 'AUTH_SESSION_EXPIRED' || err.code === 'AUTH_TOKEN_EXPIRED')
        ) {
          emitSessionExpired();
        }
        throw err;
      }
    }

    // A refreshed credential that is immediately rejected is terminal for this
    // browser session. Returning the bare response would leave protected query
    // data and the in-memory token live until an unrelated request happened to
    // fail. The session provider owns the cache purge through this event.
    if (res.status === 401 && !skipAuth && !skipRefresh && !isAuthEndpoint(url)) {
      _accessToken = null;
      emitSessionExpired();
    }

    return res;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (wasTimedOut()) {
        throw new ApiError('TIMEOUT', 0, 'errors.timeout', 'errors.timeout');
      }
      throw new ApiError('ABORTED', 0, 'errors.aborted', 'errors.aborted');
    }
    throw new ApiError(
      'NETWORK_ERROR',
      0,
      'common.errorState.networkError',
      err instanceof Error ? err.message : 'common.errorState.networkError'
    );
  } finally {
    cleanup();
  }
}

/** Canonical error code mapping by HTTP status */
const STATUS_TO_ERROR_CODE: Record<number, { code: ApiErrorCode; key: string }> = {
  400: { code: 'VALIDATION_FAILED', key: ERROR_CODE_KEYS.VALIDATION_FAILED },
  401: { code: 'AUTH_UNAUTHORIZED', key: ERROR_CODE_KEYS.AUTH_UNAUTHORIZED },
  403: { code: 'AUTH_FORBIDDEN', key: ERROR_CODE_KEYS.AUTH_FORBIDDEN },
  404: { code: 'NOT_FOUND', key: ERROR_CODE_KEYS.NOT_FOUND },
  409: { code: 'CONFLICT', key: ERROR_CODE_KEYS.CONFLICT },
  422: { code: 'UNPROCESSABLE_ENTITY', key: ERROR_CODE_KEYS.UNPROCESSABLE_ENTITY },
  429: { code: 'RATE_LIMIT_EXCEEDED', key: ERROR_CODE_KEYS.RATE_LIMIT_EXCEEDED },
  500: { code: 'INTERNAL_ERROR', key: ERROR_CODE_KEYS.INTERNAL_ERROR },
};

/**
 * Parses an error response from the API.
 *
 * Expects the canonical ApiErrorEnvelope shape produced by the global
 * exception filter. Falls back to HTTP-status-based codes for legacy/bare
 * responses, and never leaks internal stack details.
 */
export async function parseApiError(res: Response): Promise<ApiError> {
  const fallback = STATUS_TO_ERROR_CODE[res.status] ?? {
    code: 'INTERNAL_ERROR' as ApiErrorCode,
    key: ERROR_CODE_KEYS.INTERNAL_ERROR,
  };
  try {
    const body = await res.json();

    if (isApiErrorEnvelope(body)) {
      const error = body.error;
      const code = normalizeApiErrorCode(error.code);
      const key = error.messageKey ?? fallback.key;
      const message = error.message ?? res.statusText;
      return new ApiError(code, res.status, key, message, error.details, body.meta?.correlationId);
    }

    if (typeof body === 'object' && body !== null) {
      const b = body as Record<string, unknown>;
      const rawCode = (b.code ?? b.errorCode) as string | undefined;
      const code = rawCode ? normalizeApiErrorCode(rawCode) : fallback.code;
      const key = (b.messageKey ?? b.key ?? fallback.key) as string;
      const message = (b.message ?? res.statusText) as string;
      return new ApiError(code, res.status, key, message);
    }

    return new ApiError(fallback.code, res.status, fallback.key, res.statusText);
  } catch {
    return new ApiError(fallback.code, res.status, fallback.key, res.statusText);
  }
}

/**
 * apiFetch — convenience wrapper around httpClient that parses the JSON
 * response and unwraps the canonical `{ data: T }` envelope.
 *
 * Throws ApiError on non-2xx responses. Returns undefined for 204 No Content.
 */
export async function apiFetch<T>(url: string, options: HttpClientOptions = {}): Promise<T> {
  const response = await httpClient(url, options);
  if (!response.ok) throw await parseApiError(response);
  if (response.status === 204) return undefined as T;
  const body = (await response.json()) as T | { data?: T };
  if (body && typeof body === 'object' && 'data' in body && (body as { data?: T }).data !== undefined) {
    return (body as { data: T }).data;
  }
  return body as T;
}
